/**
 * One center, in full: what is declared, what is actually running, and what
 * the validator thinks of it.
 *
 * Two sections exist because of failures that are otherwise invisible:
 *
 * * **Inbound peers.** Nothing in the config file records that other centers
 *   point at this one. Deleting it silently breaks every one of them, and the
 *   breakage only shows up as failed sends days later.
 * * **The doctor row.** The subdomain label is the `doctor.id` in the shared
 *   Clinic360 database. Missing, this center is healthy to every probe and
 *   broken for every user — and if that id has not been issued yet, the
 *   doctor eventually registered with it inherits this center's images.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import {
  TextInput,
} from '../components/ui/Form';

import { Badge, StatusBadge, Tag, TriStateBadge } from '../components/Badge';
import { Callout } from '../components/Callout';
import { FindingList } from '../components/FindingList';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import { TableScroll } from '../components/SortableTable';
import {
  EmptyBlock,
  ErrorBlock,
  SkeletonRows,
  StaleBanner,
} from '../components/States';
import { ApiError, apiPost, describeError } from '../lib/api';
import {
  formatBytes,
  formatLatency,
  formatMegabytes,
  formatNumber,
  UNKNOWN,
} from '../lib/format';
import { useResource } from '../lib/useResource';
import { CopyButton } from '../components/ui/CopyButton';
import { formatDateTime } from '../lib/format';
import type {
  AffectedNode,
  CenterDetail,
  CredentialStatus,
  DateRestrictionResponse,
  IssuesResponse,
  LabelRestrictionResponse,
  ApplyResponse,
  OrthancHealth,
  RemovalPlanResponse,
  RestartResponse,
  RevealResponse,
  RotationImpactResponse,
  RotationResponse,
} from '../types/api';

const REFRESH_MS = 30_000;

function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </>
  );
}

function describeDateRestriction(rule: DateRestrictionResponse): string {
  const parts: string[] = [rule.mode];
  if (rule.days !== null) parts.push(`${rule.days} days`);
  if (rule.start_date) parts.push(`from ${rule.start_date}`);
  if (rule.end_date) parts.push(`until ${rule.end_date}`);
  return parts.join(' · ');
}

function describeLabelRestriction(rule: LabelRestrictionResponse): string {
  return `${rule.constraint}: ${rule.labels.join(', ') || 'no labels'}`;
}

function HealthCard({
  health,
  refreshing,
  onRefresh,
  error,
  probedAt,
}: {
  health: OrthancHealth | null;
  refreshing: boolean;
  onRefresh: () => void;
  error: unknown;
  /** `OrthancHealth` carries no timestamp of its own — unlike the fleet
   *  endpoints — so the freshness line uses when this client received it. */
  probedAt: Date | null;
}) {
  return (
    <Card aria-labelledby="live-health-heading">
      <div className="card__header">
        <h2 className="section__title" id="live-health-heading">
          Live health
        </h2>
        {health ? <StatusBadge state={health.state} /> : null}
        <div className="push">
          <Freshness
            checkedAt={probedAt}
            label="Probed"
            refreshing={refreshing}
            onRefresh={onRefresh}
          />
        </div>
      </div>
      <div className="card__body">
        {error && !health ? (
          <ErrorBlock
            what="live health for this center"
            error={error}
            onRetry={onRefresh}
          />
        ) : !health ? (
          <SkeletonRows rows={3} />
        ) : (
          <>
            {error ? <StaleBanner error={error} onRetry={onRefresh} /> : null}
            {health.error ? (
              <p className="center-card__error" style={{ marginBottom: '0.75rem' }}>
                <strong>Probe error:</strong> {health.error}
              </p>
            ) : null}
            <dl className="dl">
              <Row term="State">
                <StatusBadge state={health.state} />
              </Row>
              <Row term="Orthanc version">
                {health.orthanc_version ?? <span className="muted">{UNKNOWN}</span>}
              </Row>
              <Row term="DICOM AET">
                {health.dicom_aet ? (
                  <span className="mono">{health.dicom_aet}</span>
                ) : (
                  <span className="muted">{UNKNOWN}</span>
                )}
              </Row>
              <Row term="Studies">{formatNumber(health.studies)}</Row>
              <Row term="Instances">{formatNumber(health.instances)}</Row>
              <Row term="Disk size">{formatBytes(health.disk_size_bytes)}</Row>
              <Row term="Probe latency">{formatLatency(health.latency_ms)}</Row>
              <Row term="Doctor row">
                <TriStateBadge
                  value={health.doctor_row_present}
                  trueLabel="Present"
                  falseLabel="Missing"
                  nullLabel="Not checked"
                />
              </Row>
            </dl>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Rotate this center's basic-auth password.
 *
 * The response carries the only copy of the new password that will ever exist —
 * it is hashed on the way to disk and no later request can retrieve it. So this
 * component's job is to make that unmistakable, and to say plainly that nothing
 * has changed for the clinic yet: the running Caddy keeps accepting the old
 * password until a plan is applied.
 *
 * Owner-only on the server. This shows the control to everyone and lets the 403
 * explain itself, rather than hiding it — a missing button is indistinguishable
 * from a missing feature.
 */
/**
 * Which edge nodes a rotation breaks.
 *
 * Nothing else in the system notices this. A node holds the cloud password in
 * plaintext in its own `.env`, and the moment the fleet starts using a new one
 * its pushes are refused — with no error anywhere the person who rotated is
 * looking.
 *
 * `unknown` is not the same as an empty list and is never allowed to look like
 * one: a registry that could not be read tells you nothing about what will
 * break, and silently rendering "no nodes affected" would be the reassuring
 * version of "I did not check".
 */
function NodeImpact({
  nodes,
  unknown,
  error,
  done = false,
}: {
  nodes: AffectedNode[];
  unknown: boolean;
  error: string;
  done?: boolean;
}) {
  if (unknown) {
    return (
      <Callout tone="warning" title="Could not check which edge nodes this affects">
        {error} Any node replicating to this center holds the old password and
        will stop until it is updated — this list being empty means the registry
        could not be read, not that nothing breaks.
      </Callout>
    );
  }
  if (nodes.length === 0) return null;
  return (
    <Callout
      tone="warning"
      title={`${nodes.length} edge ${nodes.length === 1 ? 'node' : 'nodes'} ${
        done ? 'must now be updated' : 'will stop replicating'
      }`}
    >
      <p>
        Each holds this center's old password in <code>CLOUD_ORTHANC_PASSWORD</code>{' '}
        in its own <code>.env</code>. Until each is regenerated and its agent
        restarted, studies stay on the node and never reach the cloud.
      </p>
      <p className="row">
        {nodes.map((node) => (
          <Link
            key={node.node_uuid}
            to={`/edge-nodes/${node.node_uuid}`}
            className="tag tag--mono"
          >
            {node.label || node.node_uuid}
          </Link>
        ))}
      </p>
    </Callout>
  );
}

function RotatePanel({ slug }: { slug: string }) {
  // Fetched up front rather than on arming: the warning has to be on screen
  // while the operator is deciding, and a spinner appearing after they have
  // committed to the dialog is not a warning.
  const impact = useResource<RotationImpactResponse>(
    `/api/centers/${encodeURIComponent(slug)}/rotation-impact`,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<RotationResponse | null>(null);
  const [armed, setArmed] = useState(false);
  // Empty means "generate one", which stays the default and the recommendation.
  // The field exists because an operator sometimes has to match a password a
  // device was already configured with, and the alternative to offering it here
  // is editing caddy-auth.env by hand — which skips the bcrypt-shape check that
  // stops a malformed hash reaching the Caddyfile and stopping all 26 centers.
  const [chosen, setChosen] = useState('');
  // Trimmed only to test for emptiness: the value is sent exactly as typed,
  // because the point of the field is to match a password a device already
  // uses, and a leading or trailing space is part of that password.
  const picked = chosen.trim() ? chosen : '';
  // RotateRequest.password has min_length=12; stop it here rather than arm a
  // confirmation that can only come back as a 422.
  const tooShort = picked.length > 0 && picked.length < 12;

  async function rotate() {
    setBusy(true);
    setError(null);
    try {
      setIssued(
        await apiPost<RotationResponse>(
          `/api/centers/${encodeURIComponent(slug)}/rotate-password`,
          picked ? { password: picked } : {},
        ),
      );
      setArmed(false);
      setChosen('');
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card aria-labelledby="rotate-heading">
      <div className="card__header">
        <h2 className="section__title" id="rotate-heading">
          Credentials
        </h2>
      </div>
      <div className="card__body stack">
        {error ? (
          <Callout tone="critical" title="Could not rotate">
            {error}
          </Callout>
        ) : null}

        {issued ? (
          <>
            {issued.stored ? (
              <Callout tone="good" title="Recorded — you can reveal it again">
                Caddy still only ever sees a bcrypt hash. The password itself is
                recorded encrypted, so an owner can reveal it from this page
                instead of keeping a copy elsewhere. Every reveal is audited.
              </Callout>
            ) : (
              <Callout tone="warning" title="Copy this now — it is shown once">
                It is stored only as a bcrypt hash. Nothing here, and no later
                request, can show it again; if it is lost the only remedy is
                another rotation.
                {issued.stored_error ? ` (Recording it failed: ${issued.stored_error})` : ''}
              </Callout>
            )}
            <dl className="kv">
              <dt>Username</dt>
              <dd>
                <code>{issued.user}</code>
              </dd>
              <dt>Password</dt>
              <dd>
                <code className="secret">{issued.password}</code>
              </dd>
            </dl>
            <Callout tone="info" title="Not in effect yet">
              {issued.pending}
            </Callout>
            <NodeImpact
              nodes={issued.affected_nodes}
              unknown={issued.nodes_unknown}
              error={issued.nodes_error}
              done
            />
            {issued.removed_plaintext ? (
              <p className="muted">
                A stale plaintext <code>CENTER_{slug.toUpperCase()}_PASS</code> was also
                removed from <code>.env</code>, which the generated compose file hands to
                every container in the fleet.
              </p>
            ) : null}
          </>
        ) : armed ? (
          <>
            <Callout
              tone="warning"
              title={
                picked
                  ? `Set the password for ${slug} to the one you typed?`
                  : `Rotate the password for ${slug}?`
              }
            >
              Every device and person using the current password for this clinic will
              stop being able to log in once the change is applied.
            </Callout>
            {impact.data ? (
              <NodeImpact
                nodes={impact.data.affected_nodes}
                unknown={impact.data.nodes_unknown}
                error={impact.data.nodes_error}
              />
            ) : null}
            <div className="toolbar">
              <Button kind="danger" onClick={rotate} disabled={busy}>
                {busy ? 'Rotating…' : 'Yes, rotate it'}
              </Button>
              <Button kind="tertiary" onClick={() => setArmed(false)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="muted">
              Writes a bcrypt hash to <code>caddy-auth.env</code>. Owner role required.
            </p>
            <TextInput
              id={`rotate-chosen-${slug}`}
              labelText="Set a specific password (optional)"
              type="password"
              autoComplete="new-password"
              value={chosen}
              onChange={(event) => setChosen(event.target.value)}
              invalid={tooShort}
              invalidText="At least 12 characters"
              helperText={
                'Leave this empty to generate one, which is strongly preferred — a ' +
                'generated password is longer than one anybody types and cannot have ' +
                'been reused from somewhere else. At least 12 characters.'
              }
            />
            <div>
              <Button kind="tertiary" onClick={() => setArmed(true)} disabled={tooShort}>
                {picked ? 'Set this password' : 'Generate a new password'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Bounce this center's container.
 *
 * Stop, then up — `pacsfleet.ops` has no restart verb on purpose, since
 * `docker compose restart caddy` is one well-formed command that takes the
 * whole fleet offline and that module's defence is that the word cannot be
 * constructed at all. Both verbs refuse Caddy, and stop runs first.
 *
 * Armed rather than one-click, because the button sits next to a live clinic
 * and the cost of a mis-click is dropped connections mid-study. Same idle ->
 * armed shape as the rotate and removal panels.
 */
function RestartPanel({ slug, onRestarted }: { slug: string; onRestarted?: () => void }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RestartResponse | null>(null);

  async function restart() {
    setBusy(true);
    setError(null);
    try {
      setDone(
        await apiPost<RestartResponse>(`/api/centers/${encodeURIComponent(slug)}/restart`),
      );
      setArmed(false);
      // The health cache is short, but re-reading immediately is what makes the
      // badge agree with what just happened rather than with ten seconds ago.
      onRestarted?.();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card aria-labelledby="restart-heading">
      <div className="card__header">
        <h2 className="section__title" id="restart-heading">
          Restart
        </h2>
      </div>
      <div className="card__body stack">
        {error ? (
          <Callout tone="critical" title="Could not restart">
            {error}
          </Callout>
        ) : null}

        {done ? (
          <Callout tone="good" title={`Restarted ${done.service}`}>
            Orthanc reopens its database, rebuilds its index and reloads its plugins
            before it answers again, so the health badge can read down for a while —
            on a large center, minutes rather than seconds.
          </Callout>
        ) : null}

        {armed ? (
          <>
            <Callout tone="warning" title={`Restart ${slug} now?`}>
              Anyone sending or viewing images for this clinic loses their connection.
              Only this center is affected — Caddy and every other center keep running.
            </Callout>
            <div className="toolbar">
              <Button kind="danger" onClick={restart} disabled={busy}>
                {busy ? 'Restarting…' : 'Yes, restart it'}
              </Button>
              <Button kind="tertiary" onClick={() => setArmed(false)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="muted">
              Stops and starts this center's container, and nothing else in the fleet.
              It is not a way to deploy a change — a container comes back with the
              configuration the last apply gave it. Operator role required.
            </p>
            <div>
              <Button kind="tertiary" onClick={() => setArmed(true)}>
                Restart this center
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/** The reason every "Open Orthanc" reveal carries into the audit log. */
const OPEN_ORTHANC_REASON = 'Opened Orthanc from the fleet panel';

/**
 * How long the signed-in first step gets before the tab is moved on. The step is
 * a 401 and one retry against a few-kB JSON document; if it has not finished,
 * the move cancels it and the center shows its ordinary sign-in box, which is
 * the behaviour without this button, not a broken page.
 */
const SIGN_IN_SETTLE_MS = 2000;

/** A bare DNS name: what a center's `domain` must be before a password is put next to it. */
const HOSTNAME_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

/**
 * Open this center's Orthanc already signed in.
 *
 * It is a reveal, not a shortcut around one: the password comes from the same
 * owner-only, audited endpoint as the Recorded password panel, so every click
 * is a `center.password.reveal` row with this reason. Nothing is cached; the
 * next click asks again.
 *
 * Two steps, so the page that stays open never has the password in its URL.
 * The tab first loads `https://user:pass@{domain}/system`: the browser answers
 * Caddy's 401 with those credentials and caches them for the site. `/system`
 * is a small JSON document, so no script ever runs with that URL in
 * `location`. The tab is then replaced with `https://{domain}/`, which the
 * cached credentials sign in to, and `replace` both times leaves neither URL
 * in the tab's history. Opening the credentialed URL directly would keep the
 * password in the loaded page's address, because Orthanc's redirects are
 * relative and a relative URL inherits userinfo. Chrome and Edge sign in
 * silently; Firefox asks to confirm, and if that outlasts the settle time the
 * center shows its sign-in box instead.
 *
 * The tab keeps its `opener` for this: once a page has dropped it, Chrome
 * refuses to let the panel navigate that tab a second time ("neither
 * same-origin with the target, nor ... its opener"). What the center's page
 * gains is the ability to navigate this panel tab, and that page is our own
 * Orthanc.
 *
 * The tab is opened synchronously inside the click, while the browser still
 * counts it as user-initiated, and pointed at the center once the password
 * arrives: opened after the await, popup blockers would eat it. If the browser
 * blocks it anyway, nothing is revealed, so no audit row is spent on a click
 * that could not open anything.
 */
function OpenOrthancButton({
  slug,
  domain,
  onRevealed,
}: {
  slug: string;
  domain: string;
  onRevealed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function open() {
    setError(null);
    if (!HOSTNAME_RE.test(domain)) {
      setError(`"${domain}" is not a hostname, so there is nothing to open.`);
      return;
    }
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      setError('The browser blocked the new tab. Allow pop-ups for this site and try again.');
      return;
    }
    setBusy(true);
    try {
      const shown = await apiPost<RevealResponse>(
        `/api/centers/${encodeURIComponent(slug)}/reveal-password`,
        { reason: OPEN_ORTHANC_REASON },
      );
      onRevealed();
      if (tab.closed) {
        setError('The new tab was closed before Orthanc could open in it.');
        return;
      }
      const signIn = new URL(`https://${domain}/system`);
      signIn.username = shown.user; // the URL setters percent-encode
      signIn.password = shown.password;
      tab.location.replace(signIn.href);
      await new Promise((resolve) => window.setTimeout(resolve, SIGN_IN_SETTLE_MS));
      if (!tab.closed) tab.location.replace(`https://${domain}/`);
    } catch (cause) {
      tab.close();
      // Only the server's own words: anything else could quote the URL above.
      setError(cause instanceof ApiError ? cause.detail : 'Could not open Orthanc.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button kind="secondary" size="sm" onClick={open} disabled={busy}>
        {busy ? 'Opening…' : 'Open Orthanc'}
      </Button>
      {error ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
}

/**
 * Show the recorded password for this center.
 *
 * The panel deliberately renders its *state* before anyone asks for the value:
 * recorded or not, still valid or superseded, how many times it has been
 * revealed and by whom. Discovering "not recorded" at the moment you need the
 * password is the failure this replaces, and it is not much of an improvement
 * to move that discovery one click later.
 *
 * Owner-only on the server; the control is shown to everyone and the 403
 * explains itself, matching the rotate panel above.
 */
function RevealPanel({ slug, revealTick }: { slug: string; revealTick: number }) {
  const status = useResource<CredentialStatus>(
    `/api/centers/${encodeURIComponent(slug)}/credential`,
  );
  const { reload } = status;
  // "Open Orthanc" reveals too; re-read the counters when it does.
  useEffect(() => {
    if (revealTick > 0) reload();
  }, [revealTick, reload]);
  const [armed, setArmed] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<RevealResponse | null>(null);

  async function reveal() {
    setBusy(true);
    setError(null);
    try {
      setShown(
        await apiPost<RevealResponse>(
          `/api/centers/${encodeURIComponent(slug)}/reveal-password`,
          { reason: reason.trim() },
        ),
      );
      setArmed(false);
      setReason('');
      status.reload();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  const data = status.data;
  // The server enforces this too; matching it here keeps the button from
  // promising something the request will refuse.
  const reasonReady = reason.trim().length >= 8;

  return (
    <Card aria-labelledby="reveal-heading">
      <div className="card__header">
        <h2 className="section__title" id="reveal-heading">
          Recorded password
        </h2>
        {data?.recorded ? (
          <span className="section__count">
            revealed {formatNumber(data.revealed_count)}{' '}
            {data.revealed_count === 1 ? 'time' : 'times'}
          </span>
        ) : null}
      </div>
      <div className="card__body stack">
        {status.error && !data ? (
          <ErrorBlock
            what="the credential status"
            error={status.error}
            onRetry={status.reload}
          />
        ) : null}

        {error ? (
          <Callout tone="critical" title="Could not reveal it">
            {error}
          </Callout>
        ) : null}

        {data && !data.recorded ? (
          <Callout tone="info" title="Not recorded">
            {data.reason}
          </Callout>
        ) : null}

        {data?.recorded && !data.matches_live_hash ? (
          <Callout tone="warning" title="Superseded — do not trust it">
            {data.reason}
          </Callout>
        ) : null}

        {shown ? (
          <>
            <dl className="kv">
              <dt>Username</dt>
              <dd>
                <code>{shown.user}</code>
              </dd>
              <dt>Password</dt>
              <dd className="row">
                <code className="secret">{shown.password}</code>
                <CopyButton text={shown.password} label="Copy password" size="sm" />
              </dd>
            </dl>
            <p className="muted">
              Written to the audit log as reveal #{formatNumber(shown.revealed_count)}.
              Rotated {formatDateTime(shown.rotated_at)} by {shown.rotated_by}.
            </p>
          </>
        ) : null}

        {data?.recorded && data.revealable && !shown ? (
          armed ? (
            <>
              <TextInput
                id={`reveal-reason-${slug}`}
                labelText="Why are you looking at this?"
                helperText="Recorded in the audit log with your email. At least 8 characters."
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                autoComplete="off"
              />
              <div className="toolbar">
                <Button kind="danger" onClick={reveal} disabled={busy || !reasonReady}>
                  {busy ? 'Revealing…' : 'Reveal password'}
                </Button>
                <Button
                  kind="tertiary"
                  onClick={() => {
                    setArmed(false);
                    setReason('');
                  }}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="muted">
                Recorded {data.rotated_at ? formatDateTime(data.rotated_at) : UNKNOWN} by{' '}
                {data.rotated_by ?? UNKNOWN}
                {data.last_revealed_by
                  ? `, last revealed by ${data.last_revealed_by}`
                  : ', never revealed'}
                . Owner role required, and every reveal is audited.
              </p>
              <div>
                <Button kind="tertiary" onClick={() => setArmed(true)}>
                  Reveal password
                </Button>
              </div>
            </>
          )
        ) : null}

        {data?.recorded && !data.revealable && data.matches_live_hash ? (
          <Callout tone="info" title="Cannot be revealed on this deployment">
            {data.reason}
          </Callout>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Remove this center's routing and runtime — never its data.
 *
 * The confirm phrase is the full domain, not a plan id: destructive actions
 * earn the longer incantation. Executing echoes the previewed digest back, so
 * what runs is what was read, or nothing is. The manual-steps list is the
 * point, not a footnote: the database, the S3 prefix, the DNS record and the
 * auth entries are deliberately untouched, and saying so is what stops
 * "the panel removed it" being mistaken for "the clinic's data is gone".
 */
function RemovalPanel({ slug }: { slug: string }) {
  const [preview, setPreview] = useState<RemovalPlanResponse | null>(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyResponse | null>(null);

  async function loadPreview() {
    setBusy(true);
    setError(null);
    try {
      setPreview(await apiPost<RemovalPlanResponse>(`/api/centers/${encodeURIComponent(slug)}/removal`));
      setConfirm('');
      setResult(null);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  async function execute() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      setResult(
        await apiPost<ApplyResponse>(
          `/api/centers/${encodeURIComponent(slug)}/removal/execute`,
          { confirm, digest: preview.digest },
        ),
      );
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  const confirmed = preview !== null && confirm.trim() === preview.confirm_phrase;

  return (
    <Card aria-labelledby="removal-heading">
      <div className="card__header">
        <h2 className="section__title" id="removal-heading">
          Removal
        </h2>
      </div>
      <div className="card__body stack">
        {error ? (
          <Callout tone="critical" title="Refused">
            {error}
          </Callout>
        ) : null}

        {result ? (
          <>
            <Callout
              tone={result.state === 'applied' ? 'good' : result.needs_attention ? 'critical' : 'warning'}
              title={
                result.state === 'applied'
                  ? 'Removed — now work the manual checklist above'
                  : result.state === 'rollback_failed'
                    ? 'ROLLBACK FAILED — the fleet needs attention now'
                    : result.state === 'rolled_back'
                      ? 'Rolled back — the center is still serving'
                      : 'Refused — nothing was touched'
              }
            >
              {result.refusals.length > 0 ? result.refusals.join(' ') : null}
            </Callout>
            <ol className="steplog">
              {result.steps.map((step, index) => (
                <li key={index} className={`steplog__row steplog__row--${step.status}`}>
                  <span className="steplog__mark" aria-hidden="true">
                    {step.status === 'ok' ? '✓' : step.status === 'failed' ? '✕' : '–'}
                  </span>
                  <div>
                    <p className="steplog__name">{step.name}</p>
                    <p className="steplog__detail">{step.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </>
        ) : preview === null ? (
          <>
            <p className="muted">
              Removes this center&rsquo;s vhost, container and generated files. Its
              database, S3 prefix, DNS record and credentials are deliberately not
              touched. Preview first; nothing is written.
            </p>
            <div>
              <Button kind="tertiary" onClick={loadPreview} disabled={busy}>
                {busy ? 'Checking…' : 'Preview removal'}
              </Button>
            </div>
          </>
        ) : preview.blockers.length > 0 ? (
          <Callout tone="critical" title="Removal is blocked">
            <ul className="reasons">
              {preview.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </Callout>
        ) : (
          <>
            <Callout tone="warning" title="What will NOT be done">
              <ul className="reasons">
                {preview.manual_steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            </Callout>
            <p className="muted">
              Rewrites {preview.rewrites.length} shared {preview.rewrites.length === 1 ? 'file' : 'files'},
              removes <code>{preview.deletions.join(', ') || 'no center files'}</code> and the
              container.
            </p>
            <TextInput
              id="removal-confirm"
              className="mono"
              labelText={
                <>
                  Type <code>{preview.confirm_phrase}</code> to remove this center
                </>
              }
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <div className="toolbar">
              <Button
                kind="danger"
                onClick={execute}
                disabled={!confirmed || busy}
              >
                {busy ? 'Removing…' : 'Remove center'}
              </Button>
              <Button kind="tertiary" onClick={() => setPreview(null)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/** Keyed by slug: React Router reuses the element when only `:slug` changes
 *  (the page links to peer centers), and without a remount the previous
 *  center's data, a revealed or rotated password, and an armed removal
 *  preview would carry over onto the next center's page. */
export function CenterDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  return <CenterDetailView key={slug} slug={slug} />;
}

function CenterDetailView({ slug }: { slug: string }) {
  const [revealTick, setRevealTick] = useState(0);
  const center = useResource<CenterDetail>(`/api/centers/${encodeURIComponent(slug)}`, {
    enabled: slug.length > 0,
  });
  const health = useResource<OrthancHealth>(
    `/api/fleet/centers/${encodeURIComponent(slug)}/health`,
    { enabled: slug.length > 0, refreshMs: REFRESH_MS },
  );
  // There is no per-center findings endpoint, so the full report is fetched
  // and filtered here.
  const issues = useResource<IssuesResponse>('/api/issues', {
    enabled: slug.length > 0,
  });

  if (center.error instanceof ApiError && center.error.status === 404) {
    return (
      <>
        <PageHeader title={slug || 'Center'} />
        <EmptyBlock title={`No center is configured with the slug “${slug}”`}>
          <Link to="/centers">Back to all centers</Link>
        </EmptyBlock>
      </>
    );
  }

  if (center.loading) {
    return (
      <>
        <PageHeader title={slug} subtitle="Loading configuration…" />
        <SkeletonRows rows={5} />
      </>
    );
  }

  if (center.error && !center.data) {
    return (
      <>
        <PageHeader title={slug} />
        <ErrorBlock
          what={`the configuration for ${slug}`}
          error={center.error}
          onRetry={center.reload}
        />
      </>
    );
  }

  const data = center.data;
  if (!data) return null;

  const centerFindings =
    issues.data?.findings.filter((finding) => finding.center_slug === data.slug) ?? [];
  const doctorMissing = health.data?.doctor_row_present === false;
  // A missing row whose id is still unissued is a leak waiting for the next
  // registration; one whose id was issued (and the doctor since removed) is
  // only dead. Null is neither — a cached probe from before the field — so it
  // keeps the critical tone without claiming the leak.
  const doctorIdUnissued = health.data?.doctor_id_unissued === true;
  const doctorIdIssued = health.data?.doctor_id_unissued === false;

  return (
    <>
      <PageHeader
        title={data.slug}
        subtitle={
          <>
            {data.name ? `${data.name} · ` : null}
            <a
              href={`https://${data.domain}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              {data.domain}
            </a>
          </>
        }
        actions={
          <>
            {health.data ? <StatusBadge state={health.data.state} /> : null}
            <Link
              to={`/reports/studies?slug=${encodeURIComponent(data.slug)}`}
              className="btn btn--tertiary btn--sm"
            >
              Studies report
            </Link>
            <OpenOrthancButton
              slug={data.slug}
              domain={data.domain}
              onRevealed={() => setRevealTick((n) => n + 1)}
            />
            <Link to={`/centers/${data.slug}/edit`} className="btn btn--primary btn--sm">
              Edit
            </Link>
            <Link to="/centers" className="btn btn--tertiary btn--sm">
              All centers
            </Link>
          </>
        }
      />

      <div className="masonry">
        {doctorMissing && doctorIdUnissued ? (
          <Callout
            tone="critical"
            live
            className="masonry-wide"
            title={`The next doctor registered as #${data.domain_prefix} would see this center's images`}
          >
            <p>
              No Health-360 doctor account has{' '}
              <code>doctor.id = {data.domain_prefix}</code>, and that id is above
              every one issued so far. The gateway hands ids out in sequence, so
              whoever is registered as #{data.domain_prefix} would sign in and find
              this center&rsquo;s studies as their own.{' '}
              <a href="#removal-heading">Remove this center</a>.
            </p>
          </Callout>
        ) : doctorMissing ? (
          <Callout
            tone={doctorIdIssued ? 'warning' : 'critical'}
            live
            className="masonry-wide"
            title="This center has no doctor row"
          >
            <p>
              No Health-360 doctor account has{' '}
              <code>doctor.id = {data.domain_prefix}</code>, so nobody can sign in
              to it. Every probe on this page can be green and the center will
              still fail the moment a user opens{' '}
              <span className="mono">{data.domain}</span>. A center is created for
              a doctor&rsquo;s account; one with none serves nobody and is a
              candidate for <a href="#removal-heading">removal</a>.
            </p>
          </Callout>
        ) : null}

        {data.inbound_peers.length > 0 ? (
          <Callout
            tone="warning"
            className="masonry-wide"
            title={`${data.inbound_peers.length} other ${
              data.inbound_peers.length === 1 ? 'center sends' : 'centers send'
            } to this one`}
          >
            <p>
              Removing this center, renaming it, or changing its ports breaks
              these peers silently — nothing in their own configuration would
              show the fault.
            </p>
            <p className="row">
              {data.inbound_peers.map((peer) => (
                <Link key={peer} to={`/centers/${peer}`} className="tag tag--mono">
                  {peer}
                </Link>
              ))}
            </p>
          </Callout>
        ) : null}

        <HealthCard
          health={health.data}
          refreshing={health.refreshing}
          onRefresh={health.reload}
          error={health.error}
          probedAt={health.fetchedAt}
        />

        {/* ── declared configuration ────────────────────────────────────── */}
        <Card aria-labelledby="config-heading">
          <div className="card__header">
            <h2 className="section__title" id="config-heading">
              Configuration
            </h2>
          </div>
          <div className="card__body">
            <dl className="dl">
              <Row term="Slug">
                <span className="mono">{data.slug}</span>
              </Row>
              <Row term="Name">
                {data.name ?? <span className="muted">Unnamed</span>}
              </Row>
              <Row term="Domain">
                <span className="mono">{data.domain}</span>
              </Row>
              <Row term="Subdomain label">
                <span className="mono">{data.domain_prefix}</span>{' '}
                <span className="muted">
                  — this is the <code>doctor.id</code> in the shared database
                </span>
              </Row>
              <Row term="Web port">
                <span className="numeric">{data.web_port}</span>
              </Row>
              <Row term="DICOM port">
                <span className="numeric">{data.dicom_port}</span>
              </Row>
              <Row term="Database">
                <span className="mono">{data.database_name}</span>
              </Row>
              <Row term="S3 prefix">
                <span className="mono">{data.s3_prefix}</span>
              </Row>
              <Row term="Cache">{formatMegabytes(data.cache_ram_mb)}</Row>
              <Row term="Filter script">
                <TriStateBadge
                  value={data.has_filter}
                  trueLabel="Present"
                  falseLabel="None"
                  trueTone="info"
                  falseTone="neutral"
                />
              </Row>
              <Row term="DICOM permissions">
                <span className="row">
                  <Badge tone={data.dicom_always_allow_find ? 'info' : 'neutral'}>
                    C-FIND {data.dicom_always_allow_find ? 'always allowed' : 'restricted'}
                  </Badge>
                  <Badge tone={data.dicom_always_allow_get ? 'info' : 'neutral'}>
                    C-GET {data.dicom_always_allow_get ? 'always allowed' : 'restricted'}
                  </Badge>
                  <Badge tone={data.dicom_always_allow_move ? 'info' : 'neutral'}>
                    C-MOVE {data.dicom_always_allow_move ? 'always allowed' : 'restricted'}
                  </Badge>
                </span>
              </Row>
            </dl>
          </div>
        </Card>

        {/* ── modalities ────────────────────────────────────────────────── */}
        <Card
          aria-labelledby="modalities-heading"
          className={data.modalities.length > 0 ? 'masonry-wide' : undefined}
        >
          <div className="card__header">
            <h2 className="section__title" id="modalities-heading">
              Modalities
            </h2>
            <span className="section__count">
              {formatNumber(data.modalities.length)} configured
            </span>
          </div>
          <div className="card__body card__body--flush">
            {data.modalities.length === 0 ? (
              <div className="card__body">
                <p className="muted">
                  No modalities are configured, so no scanner can send to this
                  center by AET.
                </p>
              </div>
            ) : (
              <TableScroll label={`Modalities for ${data.slug}`}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Symbolic name</th>
                      <th scope="col">AET</th>
                      <th scope="col">Host</th>
                      <th scope="col" className="numeric">
                        Port
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.modalities.map((modality) => (
                      <tr key={`${modality.symbolic_name}-${modality.aet}`}>
                        <th scope="row">{modality.symbolic_name}</th>
                        <td className="mono">{modality.aet}</td>
                        <td className="mono">{modality.host}</td>
                        <td className="numeric">{modality.port}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </div>
        </Card>

        {/* ── restrictions ──────────────────────────────────────────────── */}
        <Card aria-labelledby="restrictions-heading">
          <div className="card__header">
            <h2 className="section__title" id="restrictions-heading">
              Restrictions
            </h2>
            <span className="section__count">
              {formatNumber(data.date_restrictions.length)} by date ·{' '}
              {formatNumber(data.label_restrictions.length)} by label
            </span>
          </div>
          <div className="card__body">
            {data.date_restrictions.length === 0 &&
            data.label_restrictions.length === 0 ? (
              <p className="muted">
                No restrictions: every configured modality may query the whole
                archive.
              </p>
            ) : (
              <div className="stack">
                {data.date_restrictions.length > 0 ? (
                  <div>
                    <h3 className="section__title">By date</h3>
                    <dl className="dl">
                      {data.date_restrictions.map((rule, index) => (
                        <Row key={`${rule.aet}-${index}`} term={rule.aet}>
                          {describeDateRestriction(rule)}
                        </Row>
                      ))}
                    </dl>
                  </div>
                ) : null}
                {data.label_restrictions.length > 0 ? (
                  <div>
                    <h3 className="section__title">By label</h3>
                    <dl className="dl">
                      {data.label_restrictions.map((rule, index) => (
                        <Row key={`${rule.aet}-${index}`} term={rule.aet}>
                          {describeLabelRestriction(rule)}
                        </Row>
                      ))}
                    </dl>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </Card>

        {/* ── peers ─────────────────────────────────────────────────────── */}
        <Card
          aria-labelledby="peers-heading"
          className={data.peers.length > 0 ? 'masonry-wide' : undefined}
        >
          <div className="card__header">
            <h2 className="section__title" id="peers-heading">
              Peers
            </h2>
            <span className="section__count">
              {formatNumber(data.peers.length)} outbound ·{' '}
              {formatNumber(data.inbound_peers.length)} inbound
            </span>
          </div>
          <div className="card__body card__body--flush">
            {data.peers.length === 0 ? (
              <div className="card__body">
                <p className="muted">This center sends to no peers.</p>
              </div>
            ) : (
              <TableScroll label={`Outbound peers for ${data.slug}`}>
                <table className="data-table">
                  <caption>Centers and systems this center sends to.</caption>
                  <thead>
                    <tr>
                      <th scope="col">Peer</th>
                      <th scope="col">Kind</th>
                      <th scope="col">Target</th>
                      <th scope="col">URL from env</th>
                      <th scope="col" className="numeric">
                        HTTP timeout
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.peers.map((peer) => (
                      <tr key={peer.peer_name}>
                        <th scope="row">{peer.peer_name}</th>
                        <td>
                          <Tag>{peer.kind}</Tag>
                        </td>
                        <td>
                          {peer.target_slug ? (
                            <Link to={`/centers/${peer.target_slug}`}>
                              {peer.target_slug}
                            </Link>
                          ) : (
                            <span className="muted">{UNKNOWN}</span>
                          )}
                        </td>
                        <td>
                          {peer.url_env ? (
                            <span className="mono">{peer.url_env}</span>
                          ) : (
                            <span className="muted">{UNKNOWN}</span>
                          )}
                        </td>
                        <td className="numeric">{peer.http_timeout}s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}

            <div className="card__body">
              <h3 className="section__title">
                Inbound peers
                <span className="section__count">
                  centers that send <em>to</em> this one
                </span>
              </h3>
              {data.inbound_peers.length === 0 ? (
                <p className="muted">
                  No other center sends to this one, so removing it would not
                  break a peer.
                </p>
              ) : (
                <p className="row">
                  {data.inbound_peers.map((peer) => (
                    <Link key={peer} to={`/centers/${peer}`} className="tag tag--mono">
                      {peer}
                    </Link>
                  ))}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* ── findings ──────────────────────────────────────────────────── */}
        <RestartPanel slug={data.slug} onRestarted={health.reload} />

        <RotatePanel slug={data.slug} />

        <RevealPanel slug={data.slug} revealTick={revealTick} />

        <RemovalPanel slug={data.slug} />

        <Card
          aria-labelledby="findings-heading"
          className={centerFindings.length > 0 ? 'masonry-wide' : undefined}
        >
          <div className="card__header">
            <h2 className="section__title" id="findings-heading">
              Findings
            </h2>
            {issues.data ? (
              <span className="section__count">
                {formatNumber(centerFindings.length)} for this center
              </span>
            ) : null}
            <div className="push">
              <Link to="/issues" className="btn btn--ghost btn--sm">
              Full report
            </Link>
            </div>
          </div>
          <div className="card__body card__body--flush">
            {issues.loading ? (
              <div className="card__body">
                <SkeletonRows rows={2} />
              </div>
            ) : issues.error && !issues.data ? (
              <div className="card__body">
                <ErrorBlock
                  what="the validator report"
                  error={issues.error}
                  onRetry={issues.reload}
                />
              </div>
            ) : centerFindings.length === 0 ? (
              <div className="card__body">
                <p className="muted">
                  The validator reported nothing for this center.
                  {issues.data?.auth_rules_skipped ? (
                    <>
                      {' '}
                      Note that auth rules were skipped in this run, so the
                      report is incomplete.
                    </>
                  ) : null}
                </p>
              </div>
            ) : (
              <>
                {issues.data?.auth_rules_skipped ? (
                  <div className="card__body">
                    <Callout tone="warning" title="This report is incomplete">
                      <p>
                        Auth rules were skipped, so authentication problems for
                        this center would not appear below.
                      </p>
                    </Callout>
                  </div>
                ) : null}
                <FindingList
                  findings={centerFindings}
                  showCenter={false}
                  label={`Findings for ${data.slug}`}
                />
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
